import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import moment from 'moment';

import { BE_MY_GUEST_VOUCHER_DESCRIPTION, VOUCHER_TYPE_DISCOUNT } from 'constants/vouchers';
import { GET_VOUCHER_BY_TYPE_AND_PERIOD } from 'store/queries';
import { FullPageSpinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import SortableTable from 'components/ui/SortableTable';
import { colours } from 'themes/default';

import { StyledContainer, StyledSummaryContainer } from './BeMySpecialGuestReport.styles';

const columns = [
  {
    key: 'createdAt',
    intlId: 'beMyGuestResults.createdAt',
    format: (createdAt) => moment(createdAt).format('ddd DD MMM YYYY')
  },
  {
    key: 'createdBy',
    intlId: 'beMyGuestResults.createdBy'
  },
  {
    key: 'reference',
    intlId: 'beMyGuestResults.reference',
    format: (reference) => (
      <Link target="_blank" to={`/discountCodes/${reference}`}>
        {reference}
      </Link>
    )
  },
  {
    key: 'value',
    intlId: 'beMyGuestResults.value',
    format: (value, { currency }) => <FormattedCurrency amount={value} currency={currency} />
  },
  {
    key: 'relatedBookingNumber',
    intlId: 'beMyGuestResults.relatedBookingNumber',
    format: (reference) => (
      <Link target="_blank" to={`/manage/bookings/${reference}`}>
        {reference}
      </Link>
    )
  }
];

const BeMySpecialGuestReport = () => {
  const currentDate = new Date();
  const fromDate = `${currentDate.getFullYear()}-01-01`;
  const toDate = currentDate.toISOString().split('T')[0];

  const { loading, error, data } = useQuery(GET_VOUCHER_BY_TYPE_AND_PERIOD, {
    variables: {
      fromDate,
      toDate,
      active: true,
      description: BE_MY_GUEST_VOUCHER_DESCRIPTION,
      voucherType: VOUCHER_TYPE_DISCOUNT
    }
  });

  const vouchersInfo = useMemo(() => {
    if (data?.getVouchersByTypeAndPeriod?.vouchers && data.getVouchersByTypeAndPeriod.vouchers.length > 0) {
      const vouchers = data.getVouchersByTypeAndPeriod.vouchers;

      return {
        vouchers,
        totalAmount: vouchers.reduce((acc, voucher) => {
          if (acc[voucher.currency]) {
            acc[voucher.currency] += voucher.value;
          } else {
            acc[voucher.currency] = voucher.value;
          }

          return acc;
        }, {})
      };
    }

    return null;
  }, [data]);

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: '/vouchers/be-my-special-guest-report'
    });
  }

  if (loading) {
    return <FullPageSpinner data-testid="vouchers-be-my-guest-report-loading" />;
  }

  return (
    <StyledContainer>
      <StyledSummaryContainer>
        <h1 data-testid="be-my-guest-report-title">
          <FormattedMessage id="beMyGuestResults.title" />
        </h1>
        <span data-testid="be-my-guest-report-result-message">
          <FormattedMessage id="bookingSearchResults.summary.weFound" /> {vouchersInfo?.vouchers?.length ?? 0}{' '}
          <FormattedMessage id="bookingSearchResults.summary.resultsFor" />: <FormattedMessage id="beMyGuestResults.cratedFromDate" /> ={' '}
          {moment(fromDate).format('ddd DD MMM YYYY')}, <FormattedMessage id="beMyGuestResults.cratedToDate" /> ={' '}
          {moment(toDate).format('ddd DD MMM YYYY')}.
        </span>
        {vouchersInfo?.totalAmount && Object.keys(vouchersInfo.totalAmount).length > 0 && (
          <span data-testid="be-my-guest-report-total-message">
            <FormattedMessage id="summary.total" />:{' '}
            {Object.keys(vouchersInfo.totalAmount).reduce((acc, key, index, array) => {
              acc.push(<FormattedCurrency key={key} amount={vouchersInfo.totalAmount[key]} currency={key} />);

              if (index < array.length - 1) {
                acc.push('; ');
              }
              return acc;
            }, [])}
          </span>
        )}
      </StyledSummaryContainer>
      {vouchersInfo?.vouchers && vouchersInfo?.vouchers?.length > 0 && (
        <SortableTable
          formatHeader={({ intlId }) => <FormattedMessage id={intlId} />}
          data={vouchersInfo.vouchers}
          columns={columns}
          sortKey="createdAt"
          borderColor={colours.Greyscale.CharcoalGrey}
          alternateBackground={colours.Messaging.SuccessGreenLight}
        />
      )}
    </StyledContainer>
  );
};

export default BeMySpecialGuestReport;
